import 'src/utils/init-compromise-dates';

import nlp from 'compromise';
import dayjs from 'dayjs';

interface CompromiseContext {
  dayStart?: string;
  timezone?: string;
}

interface CompromiseDate {
  start: string;
  end: string;
}

type CompromiseDuration = Record<string, number>;

interface CompromiseDatesView {
  get(): unknown[];
}

interface CompromiseDurationsView {
  get(): unknown[];
}

interface CompromiseDoc {
  dates(context?: CompromiseContext): CompromiseDatesView;
  durations(): CompromiseDurationsView;
}

const parseNaturalLanguage: (input: string) => unknown = nlp as unknown as (
  input: string,
) => unknown;

function isCompromiseDoc(value: unknown): value is CompromiseDoc {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<CompromiseDoc>;
  return typeof candidate.dates === 'function' && typeof candidate.durations === 'function';
}

function isCompromiseDate(value: unknown): value is CompromiseDate {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<CompromiseDate>;
  return typeof candidate.start === 'string' && typeof candidate.end === 'string';
}

function isCompromiseDuration(value: unknown): value is CompromiseDuration {
  if (!value || typeof value !== 'object') {
    return false;
  }

  return Object.values(value).every(
    (entry) => typeof entry === 'number' && Number.isFinite(entry),
  );
}

function getDatesOptionsFromNaturalLanguage(
  naturalLanguageDate: string,
  context?: CompromiseContext,
): CompromiseDate[] {
  const candidate = parseNaturalLanguage(naturalLanguageDate);
  if (!isCompromiseDoc(candidate)) {
    return [];
  }

  const document: CompromiseDoc = candidate;
  const datesView = document.dates(context);
  const datesRaw = datesView.get();
  const datesArray = Array.isArray(datesRaw) ? datesRaw.filter(isCompromiseDate) : [];
  const durationsView = document.durations();
  const durationsRaw = durationsView.get();
  const durationsArray = Array.isArray(durationsRaw)
    ? durationsRaw.filter(isCompromiseDuration)
    : [];

  return datesArray.length ? datesArray : getDatesFromDuration(durationsArray);
}

function getDatesFromDuration(durationsArray: CompromiseDuration[]): CompromiseDate[] {
  return durationsArray.map((duration) => {
    let currentDate = dayjs();
    Object.entries(duration).forEach(([key, value]) => {
      currentDate = currentDate.add(value, key as dayjs.ManipulateType);
    });

    const resolved = currentDate.toString();

    return {
      start: resolved,
      end: resolved,
    };
  });
}

export function getDateFromNaturalLanguage(
  naturalLanguageDate: string,
  context?: CompromiseContext,
): string {
  const sanitized = sanitizeNaturalLanguageInput(naturalLanguageDate);
  const dates = getDatesOptionsFromNaturalLanguage(sanitized, context);
  const date = dates.at(-1)?.start ?? '';
  if (date) {
    return date;
  }

  // fallback aliases (noon, EOD, tonight, morning, afternoon, evening)
  const fallback = resolveAliasDate(sanitized.trim().toLowerCase());
  return fallback ?? '';
}

/**
 * There are some edge cases that are not handled correctly by compromise when the dayStart
 * param is added. For example, "in 30 minutes" or "in 1 hour". Adding the dayStart param in
 * those scenarios will take the value of dayStart instead of the expected "in 30 minutes".
 * This helper compares the parsed values and returns the one that better represents the
 * user's intent.
 */
export function resolveNaturalLanguageDate(
  parsedWithDayStart: string,
  parsedWithoutDayStart: string,
): string {
  const dateWithDayStart = new Date(parsedWithDayStart);
  const dateWithoutDayStart = new Date(parsedWithoutDayStart);

  if (
    dateWithoutDayStart > dateWithDayStart ||
    !parsedWithoutDayStart.includes('00:00:00.000')
  ) {
    return parsedWithoutDayStart;
  }

  return parsedWithDayStart;
}

/**
 * Adjusts past dates to keep the schedule in the future.
 * For time-only inputs (e.g., "8am"), rolls forward by 1 day.
 * For weekday-only inputs (e.g., "Wed"), keeps the next available occurrence.
 * For other inputs, rolls forward by 1 year.
 */
export function ensureFutureDate(parsedDate: string, originalInput?: string): string {
  const currentDate = new Date();
  const date = new Date(parsedDate);

  if (originalInput && isWeekdayOnlyInput(originalInput)) {
    if (!isAfterCalendarDay(date, currentDate)) {
      date.setDate(date.getDate() + 7);
    }
  } else if (date < currentDate) {
    if (originalInput && isTimeOnlyInput(originalInput)) {
      date.setDate(date.getDate() + 1);
    } else {
      date.setFullYear(date.getFullYear() + 1);
    }
  }

  return date.toISOString();
}

/**
 * Determines if the input appears to be a time-only expression (e.g., "8am", "at 8am", "3:30pm", "20:00").
 * These should default to the next occurrence (tomorrow if already past today).
 */
function isTimeOnlyInput(input: string): boolean {
  const sanitized = sanitizeNaturalLanguageInput(input).toLowerCase().trim();
  if (!sanitized) return false;

  // Patterns that indicate time-only input (no date component)
  // - "8am", "8 am", "8:00am", "8:00 am", "at 8am", "at 8:00 am"
  // - "3pm", "3 pm", "3:30pm", "3:30 pm", "at 3pm"
  // - "20:00", "08:00", "at 20:00"
  // - "noon", "midnight", "at noon"
  const timeOnlyPatterns = [
    /^(?:at\s+)?\d{1,2}(?::\d{2})?\s*(?:am|pm)$/i,
    /^(?:at\s+)?\d{1,2}:\d{2}$/,
    /^(?:at\s+)?(?:noon|midnight)$/i,
  ];

  return timeOnlyPatterns.some((pattern) => pattern.test(sanitized));
}

function isWeekdayOnlyInput(input: string): boolean {
  const sanitized = sanitizeNaturalLanguageInput(input).toLowerCase().trim();
  if (!sanitized) return false;

  return /^(?:mon(?:day)?|tue(?:s|sday)?|wed(?:s|nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)$/.test(
    sanitized,
  );
}

function isAfterCalendarDay(date: Date, currentDate: Date): boolean {
  const startOfDate = new Date(date);
  startOfDate.setHours(0, 0, 0, 0);

  const startOfCurrentDate = new Date(currentDate);
  startOfCurrentDate.setHours(0, 0, 0, 0);

  return startOfDate > startOfCurrentDate;
}

function resolveAliasDate(text: string): string | null {
  if (!text) return null;

  // simple aliases
  const now = dayjs();
  const setTime = (h: number, m = 0) =>
    now.set('hour', h).set('minute', m).set('second', 0).set('millisecond', 0);

  const aliasMap: (
    | { match: RegExp; time: () => dayjs.Dayjs }
    | { match: RegExp; compute: () => dayjs.Dayjs }
  )[] = [
    { match: /\bnoon\b/, time: () => setTime(12, 0) },
    { match: /\bmorning\b/, time: () => setTime(9, 0) },
    { match: /\bafternoon\b/, time: () => setTime(15, 0) },
    { match: /\bevening\b|\btonight\b/, time: () => setTime(18, 0) },
    { match: /\beod\b|end of day/, time: () => setTime(17, 0) },
    {
      match: /next business day/,
      compute: () =>
        now
          .add(1, 'day')
          .set('hour', 9)
          .set('minute', 0)
          .set('second', 0)
          .set('millisecond', 0),
    },
  ];

  for (const rule of aliasMap) {
    if (rule.match.test(text)) {
      const target = 'time' in rule ? rule.time() : rule.compute();
      const future = target.isAfter(now) ? target : target.add(1, 'day');
      return future.toISOString();
    }
  }

  return null;
}

/**
 * Sanitizes user-entered phrases to improve natural-language parsing.
 * Examples:
 *  - "send in a week"  -> "in a week"
 *  - "send at 5pm"     -> "at 5pm"
 *  - "please schedule tomorrow morning" -> "tomorrow morning"
 */
function sanitizeNaturalLanguageInput(input: string): string {
  if (!input) return '';
  let text = String(input).trim();

  // Normalize common verb + preposition openings
  // "send in 2 hours" / "sendin 2 hours" / "sending in 2 hours" -> "in 2 hours"
  text = text.replace(/^\s*(please\s+)?(send|sending)\s*in\s+/i, 'in ');
  // "send at 5pm" / "sending at 5pm" -> "at 5pm"
  text = text.replace(/^\s*(please\s+)?(send|sending)\s*at\s+/i, 'at ');

  // Generic stripping of action verbs at the very beginning
  text = text.replace(
    /^\s*(please\s+)?(send|sending|schedule|snooze|remind|set|create|make)\s+(?:it\s+|this\s+|message\s+)?/i,
    '',
  );

  // Remove leading pronouns if left after previous rule
  text = text.replace(/^\s*(me\s+|to\s+me\s+)/i, '');

  // Trim common trailing punctuation
  text = text.replace(/[.,!?\s]+$/g, '').trim();

  const normalizedCompactTime = normalizeCompactMeridiemTimes(text);
  if (!normalizedCompactTime) {
    return '';
  }

  text = normalizedCompactTime;

  return text;
}

function normalizeCompactMeridiemTimes(input: string): string | null {
  let hasInvalidCompactTime = false;

  const normalized = input.replace(
    /(^|\s)(at\s+)?(\d{3,4})\s*(am|pm)\b/gi,
    (match, ...groups) => {
      const [leadingWhitespace, atPrefix, digits, meridiem] = groups as [
        string,
        string | undefined,
        string,
        string,
      ];
      const hourText = digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2);
      const minuteText = digits.slice(-2);
      const hour = Number(hourText);
      const minute = Number(minuteText);

      if (hour < 1 || hour > 12 || minute > 59) {
        hasInvalidCompactTime = true;
        return match;
      }

      return `${leadingWhitespace}${atPrefix ?? ''}${hour}:${minuteText} ${meridiem.toLowerCase()}`;
    },
  );

  return hasInvalidCompactTime ? null : normalized;
}
